import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
// components
import Tab from "./Tab";
import DistanceLocationInput from "../inputs/DistanceLocationInput";
import Select from "../Select";
import TabContent from "./TabContent";
// @interfaces
import { STEPS } from "../../@interfaces/tabs.interface";
// utils
import {
  defaultValues,
  motorbikeTypes,
  motorbikeVehicleTypes,
  publicTransportTypes,
  vehicleSizes,
  vehicleTypes
} from "../../utils/tabsConfig";
// @interfaces & @types
import {
    CabinClass,
  IFlightLeg,
  IQuoteBySurfaceTravelPrivateData,
  IQuoteBySurfaceTravelPublicData,
  IQuoteFixed
} from "../../@interfaces/quote.interface";
import { IMerchantConfig } from "../../@interfaces/config.interface";
import { CALCULATOR_MODE } from "../../@types/travel";
// utils
import { handleUrlParameters } from "../../utils/calculations";
// styles
import styles from "../../styles/Tabs.module.css";
import DurationInput from "../inputs/DurationInput";
import { AppButton } from "../app-button";
import { AppFormProvider } from "../form/app-form-provider";
import { FlightTabContent } from "./flight-tab-content";


interface TabsProps {
  merchantCode: string;
  changeTab: (tab: string) => void;
  activeTab: string;
  onOffsetClick: any;
  onFixedOffsetClick: (requestBody: IQuoteFixed) => void;
  selectedCurrency: string;
  isLoading: boolean;
  urlParams: {
    [string: string]: string;
  };
  merchantConfig: IMerchantConfig;
}

const averageSpeedKmh: { [key: string]: number; } = {
  "TRAIN": 200,
  "BUS": 80,
  "FERRY": 40,
  "MOTORBIKE": 130,
  "SCOOTER": 40,
  "CAR": 130
};

const getCo2PerHour = (vehicleClass: string, vehicleType: string, vehicleSize?: string) => {
  if (vehicleClass === STEPS.CAR) {
    if (vehicleType === "ELECTRIC") {
      return 0.064 * 60; // Electric car: 0.064kg of CO2 per minute
    } else if (vehicleSize === "SMALL") {
      return 0.167 * 60; // Small car: 0.167kg of CO2 per minute
    } else if (vehicleSize === "LARGE") {
      return 0.402 * 60; // Large car: 0.402 kg of CO2 per minute
    } else {
      return 0.200 * 60; // Medium car: 0.2kg of CO2 per minute
    }
  } else if (vehicleClass === STEPS.PUBLIC_TRANSPORT) {
    if (vehicleType === "TRAIN") {
      return 0.023 * 60; // Metro: 0.023kg of co2 per minute
    } 
  } else {
    return null;
  }
}

type Display2Row = {
  [key: string]: boolean;
}

const Tabs: React.FC<TabsProps> = ({
  merchantCode,
  changeTab,
  activeTab,
  onOffsetClick,
  onFixedOffsetClick,
  selectedCurrency,
  isLoading,
  urlParams,
  merchantConfig,
}) => {

  const [useTravelTime,setUseTravelTime] = useState(false);
  const [calculatorMode, setCalculatorMode] = useState<string>(CALCULATOR_MODE.return);
  const [distanceError, setDistanceError] = useState('');
  const [isFlightsValid, setIsFlightsValid] = useState(false);
  const [display2Row, setDisplay2Row] = useState<Display2Row>({
    [STEPS.FLIGHT]: false,
    [STEPS.CAR]: false,
    [STEPS.PUBLIC_TRANSPORT]: false,
    [STEPS.MOTORBIKE]: false,
  });

  const methods = useForm<FieldValues>({ defaultValues });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
  } = methods;

  const flightsCabinClass = watch("flights.cabinClass");
  const flights = watch("flights.values");
  const flightsDuration = watch("flights.duration");

  const carFrom = watch("car.origin");
  const carTo = watch("car.destination");
  const carVehicleSize = watch("car.vehicleSize");
  const carVehicleType = watch("car.vehicleType");
  const carDistance = watch("car.distance");
  const carCountry = watch("car.country");
  const carDuration = watch("car.duration");

  const publicTransportFrom = watch("publicTransport.origin");
  const publicTransportTo = watch("publicTransport.destination");
  const publicTransportType = watch("publicTransport.transportType");
  const publicTransportDistance = watch("publicTransport.distance");
  const publicTransportCountry = watch("publicTransport.country");
  const publicTransportDuration = watch("publicTransport.duration");

  const motorbikeFrom = watch("motorbike.origin");
  const motorbikeTo = watch("motorbike.destination");
  const motorbikeType = watch("motorbike.motorbikeType");
  const motorbikeVehicleType = watch("motorbike.vehicleType");
  const motorbikeDistance = watch("motorbike.distance");
  const motorbikeCountry = watch("motorbike.country");
  const motorbikeDuration = watch("motorbike.duration");

  const flightsString = JSON.stringify(flights);

  const isValid = useMemo(() => {
    const hasEmpty = flights?.some((flight: IFlightLeg) => flight.origin === "" || flight.destination === "");
    const originIsSameAsDestination = flights?.some((flight: IFlightLeg) => flight.origin === flight.destination);

    if (!hasEmpty && originIsSameAsDestination) {
      setValue('flights.errorMessage', 'Please, make sure that origin and destination are different.');
    } else {
      setValue('flights.errorMessage', '');
    }
    
    return hasEmpty || originIsSameAsDestination;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flights, flightsString]);

  useEffect(() => {
    setIsFlightsValid(!isValid)
  }, [isValid])

  const setCustomValue = (id: string, value: any) => {
    setValue(id, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    handleUrlParameters(urlParams, setCustomValue, setCalculatorMode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams]);

  const carTravelRequest = useMemo<IQuoteBySurfaceTravelPrivateData>( () => {
    const distanceKm = useTravelTime ? (
      carDuration * averageSpeedKmh["CAR"]
    ) : (
      carDistance / 1000
    );
    return {
      distance: {
        value: distanceKm,
        unit: "KM" as const
      },
      currency: selectedCurrency,
      merchantCode: merchantCode,
      paymentProvider: "STRIPE",
      transportMethod: "CAR",
      category: carVehicleSize?.value,
      quantity: 1,
      powerTrain: carVehicleType?.value,
      maximumFeesRatio: 0.4,
      countryCode: carCountry,
      activityMetadata: {
        travels: [{
          duration: carDuration,
          transportMethod: "CAR",
          "origin": carFrom,
          "destination": carTo,
        }]
      }
    }
  },[carDistance, selectedCurrency, merchantCode, carVehicleSize, carVehicleType, carFrom, carTo, carCountry, useTravelTime, carDuration]);

  const motorbikeTravelRequest = useMemo<IQuoteBySurfaceTravelPrivateData>( () => {
    const distanceKm = useTravelTime ? (
      motorbikeDuration * averageSpeedKmh[motorbikeType?.value]
    ) : (
      motorbikeDistance / 1000
    );
    return {
      distance: {
        value: distanceKm,
        unit: "KM" as const
      },
      currency: selectedCurrency,
      merchantCode: merchantCode,
      paymentProvider: "STRIPE",
      transportMethod: motorbikeType?.value,
      quantity: 1,
      powerTrain: motorbikeVehicleType?.value,
      maximumFeesRatio: 0.4,
      countryCode: motorbikeCountry,
      activityMetadata: {
        travels: [{
          duration: motorbikeDuration,
          transportMethod: motorbikeType?.value,
          "origin": motorbikeFrom,
          "destination": motorbikeTo,
        }]
      }
    }
  },[selectedCurrency, merchantCode, motorbikeType, motorbikeVehicleType, motorbikeDistance, motorbikeFrom, motorbikeTo, motorbikeCountry, motorbikeDuration, useTravelTime]);


  const publicTransportTravelRequest = useMemo<IQuoteBySurfaceTravelPublicData>( () => {
    //hardcoded value, need to provide selector for the category.
    const category = publicTransportType?.value === "TRAIN" ? "NATIONAL" : undefined;
    const distanceKm = useTravelTime ? (
        publicTransportDuration * averageSpeedKmh[publicTransportType?.value]
    ) : (
        publicTransportDistance / 1000
    );
    return {
      distance: {
        value: distanceKm,
        unit: "KM" as const
      },
      currency: selectedCurrency,
      merchantCode: merchantCode,
      paymentProvider: "STRIPE",
      transportMethod: publicTransportType?.value,
      category: category,
      passengers: 1,
      maximumFeesRatio: 0.4,
      countryCode: publicTransportCountry,
      activityMetadata: {
        travels: [{
          duration: publicTransportDuration,
          transportMethod: publicTransportType?.value,
          "origin": publicTransportFrom,
          "destination": publicTransportTo,
        }]
      }
    }
  },[publicTransportDistance, selectedCurrency, merchantCode, publicTransportType, publicTransportFrom, publicTransportTo, publicTransportCountry, useTravelTime, publicTransportDuration]);

  const getFlightsPayload = (payload: { values: IFlightLeg[], duration: number, passengers: number }) => {
    const { duration, passengers, values } = payload;
    let flights: IFlightLeg[] | undefined = undefined;
    let flightHours: number | undefined = undefined;
    let cabinClass: CabinClass | undefined = undefined;
    if (useTravelTime) {
      flightHours = duration * (calculatorMode === CALCULATOR_MODE.return ? 2 : 1);
      cabinClass = flightsCabinClass?.value;
    } else {
      switch (calculatorMode) {
        case CALCULATOR_MODE.oneway:
          flights = [values[0]].map((flight: IFlightLeg) => ({ ...flight, cabinClass: flightsCabinClass?.value }));
          break;
        case CALCULATOR_MODE.return:
          flights = [values[0]].map((flight: IFlightLeg) => ({ ...flight, cabinClass: flightsCabinClass?.value })).concat([{
            origin: values[0].destination,
            destination: values[0].origin,
            cabinClass: flightsCabinClass?.value,
          }]);
          break;
        case CALCULATOR_MODE.multicity:
          flights = values.map((flight: IFlightLeg) => ({ ...flight, cabinClass: flightsCabinClass?.value }));
          break;
      }
    }
    return {
      flights,
      flightHours,
      cabinClass,
      merchantCode,
      passengers,
    };
  }

  const onSubmit = async (payload: FieldValues) => {
    let vehicleType;
    let co2perHour;
    if (useTravelTime) {
      vehicleType = ({
        [STEPS.CAR]: carVehicleType.value,
        [STEPS.FLIGHT]: flightsCabinClass.value,
        [STEPS.MOTORBIKE]: motorbikeVehicleType.value,
        [STEPS.PUBLIC_TRANSPORT]: publicTransportType.value
      })[activeTab];

      const vehicleSize = ({
        [STEPS.CAR]: carVehicleSize,
      })[activeTab];

      co2perHour = getCo2PerHour(activeTab, vehicleType, vehicleSize)
    }

    if (co2perHour) {
      const activityMetadata = ({
        [STEPS.CAR]: carTravelRequest.activityMetadata,
        [STEPS.FLIGHT]: null,
        [STEPS.MOTORBIKE]: motorbikeTravelRequest.activityMetadata,
        [STEPS.PUBLIC_TRANSPORT]: publicTransportTravelRequest.activityMetadata
      })[activeTab];
      const duration = ({
        [STEPS.CAR]: carDuration,
        [STEPS.FLIGHT]: flightsDuration,
        [STEPS.MOTORBIKE]: motorbikeDuration,
        [STEPS.PUBLIC_TRANSPORT]: publicTransportDuration
      })[activeTab];
      const offsetType = ({
        [STEPS.CAR]: "CAR",
        [STEPS.FLIGHT]: "FLIGHT",
        [STEPS.MOTORBIKE]: "MOTORBIKE",
        [STEPS.PUBLIC_TRANSPORT]: "PUBLIC"
      })[activeTab];
      onFixedOffsetClick({
          transportMethod: offsetType,
          merchantCode,
          co2Quantity: duration * co2perHour,
          activityMetadata: JSON.stringify(activityMetadata)
      })
    } else {
      switch (activeTab) {
        case STEPS.FLIGHT:
          onOffsetClick(getFlightsPayload(payload.flights), "FLIGHT", calculatorMode);
          break;
        case STEPS.CAR:
          onOffsetClick(carTravelRequest, "PRIVATE");
          break;
        case STEPS.PUBLIC_TRANSPORT:
          onOffsetClick(publicTransportTravelRequest, "PUBLIC");
          break;
        case STEPS.MOTORBIKE:
          onOffsetClick(motorbikeTravelRequest, "PRIVATE");
          break;
      }
    }
  }

  const ModeSwitcher = () => {
    const buttonEnables = !useTravelTime;
    return (<button 
      className={`${styles.calculatorModeSwitch}`}
      style={{ marginTop: '1rem' }}
      onClick={()=>{ setUseTravelTime(buttonEnables); setDisplay2Row({
        [STEPS.FLIGHT]: buttonEnables,
        [STEPS.CAR]: buttonEnables,
        [STEPS.PUBLIC_TRANSPORT]: buttonEnables,
        [STEPS.MOTORBIKE]: buttonEnables,
      }) }}
    >
      {buttonEnables ? "Calculate by travel time" : activeTab === STEPS.FLIGHT ? "Use airport names instead" : "Use city names instead"}
    </button>)
  }

  return (
    <AppFormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
    <div className={`${styles.tabsWrapper} ${display2Row[activeTab] ? styles.isNarrow : ''}`}>      
      <div className={styles.tabPanels}>
        {merchantConfig?.tabsConfig?.map((tab) => {
          return (
            <Tab
              key={`tab-${tab?.name}`}
              name={tab?.name}
              tab={tab}
              displaySecondRow={display2Row[activeTab]}
              changeTab={changeTab}
              activeTab={activeTab}
            />
          );
        })}
      </div>

      {activeTab === STEPS.CAR && (
        <TabContent
          activeTab={activeTab}
          fieldKeyFrom={carFrom}
          fieldKeyTo={carTo}
          setCustomValue={setCustomValue}
          distanceError={distanceError}
          setDistanceError={setDistanceError}
          display2Row={display2Row[STEPS.CAR]}
          setDisplay2Row={(val) => setDisplay2Row(prev => ({ ...prev, [STEPS.CAR]: val }))}
          FirstLineComponent={useTravelTime ? (
            <DurationInput
              label="How long did the trip take?"
              value={carDuration}
              onChange={(value) => { setCustomValue("car.duration", value) }}
              minHours={0.5}
              maxHours={8}
              rightCorner={<ModeSwitcher />}
            />
          ) : (<>
          <DistanceLocationInput
            label="From"
            value={carFrom}
            placeholder={"Departure"}
            setCalculationError={setDistanceError}
            onChange={(value, country) => { setCustomValue("car.origin", value); setCustomValue("car.country", country) }}
          />
          <DistanceLocationInput
            label="To"
            value={carTo}
            placeholder={"Destination"}
            setCalculationError={setDistanceError}
            onChange={(value) => setCustomValue("car.destination", value)}
          /></>)}
          ComponentUnder={<ModeSwitcher/>}
          useTravelTime={useTravelTime}
          distance={'car.distance'}
        >
          <div className={styles.fieldRow}>
            <Select
              onChange={(value) => setCustomValue("car.vehicleSize", value)}
              id="vehicleSize"
              label="Vehicle size"
              optionsList={vehicleSizes}
              register={register}
              selectedItem={carVehicleSize}
            />
            <Select
              onChange={(value) => setCustomValue("car.vehicleType", value)}
              id="vehicleType"
              label="Vehicle type"
              optionsList={vehicleTypes}
              register={register}
              selectedItem={carVehicleType}
            />
          </div>
        </TabContent>
      )}

      {activeTab === STEPS.FLIGHT && (
        <FlightTabContent
          useTravelTime={useTravelTime}
          ModeSwitcher={<ModeSwitcher />}
          calculatorMode={calculatorMode}
          setCalculatorMode={setCalculatorMode}
          display2Row={display2Row[STEPS.FLIGHT]}
          setDisplay2Row={(val) => setDisplay2Row(prev => ({ ...prev, [STEPS.FLIGHT]: val }))}
        />
      )}

      {activeTab === STEPS.PUBLIC_TRANSPORT &&
        <TabContent
          activeTab={activeTab}
          fieldKeyFrom={publicTransportFrom}
          fieldKeyTo={publicTransportTo}
          setCustomValue={setCustomValue}
          distanceError={distanceError}
          setDistanceError={setDistanceError}
          display2Row={display2Row[STEPS.PUBLIC_TRANSPORT]}
          setDisplay2Row={(val) => setDisplay2Row((prev: any) => ({ ...prev, [STEPS.PUBLIC_TRANSPORT]: val }))}
          FirstLineComponent={useTravelTime ? (
            <DurationInput
              label="How long did the trip take?"
              value={publicTransportDuration}
              onChange={(value) => { setCustomValue("publicTransport.duration", value); }}
              minHours={0.5}
              maxHours={12}
              rightCorner={<ModeSwitcher />}
            />
          ) : (<><DistanceLocationInput
            label="From"
            value={publicTransportFrom}
            placeholder={"Departure"}
            setCalculationError={setDistanceError}
            onChange={(value, country) => { setCustomValue("publicTransport.origin", value); setCustomValue("publicTransport.country", country) }}
          />
          <DistanceLocationInput
            label="To"
            value={publicTransportTo}
            placeholder={"Destination"}
            setCalculationError={setDistanceError}
            onChange={(value) => setCustomValue("publicTransport.destination", value)}
          /></>)}
          ComponentUnder={<ModeSwitcher/>}
          useTravelTime={useTravelTime}
          distance={'publicTransport.distance'}
        >
          <div className={styles.fieldRow}>
            <Select
              onChange={(value) => setCustomValue("publicTransport.transportType", value)}
              id="type"
              label="Transport type"
              optionsList={publicTransportTypes}
              register={register}
              selectedItem={publicTransportType}
            />
          </div>
        </TabContent>
      }

      {activeTab === STEPS.MOTORBIKE && (
        <TabContent
          activeTab={activeTab}
          fieldKeyFrom={motorbikeFrom}
          fieldKeyTo={motorbikeTo}
          setCustomValue={setCustomValue}
          distanceError={distanceError}
          setDistanceError={setDistanceError}
          display2Row={display2Row[STEPS.MOTORBIKE]}
          setDisplay2Row={(val) => setDisplay2Row(prev => ({ ...prev, [STEPS.MOTORBIKE]: val }))}
          FirstLineComponent={useTravelTime ? (
            <DurationInput
              label="How long did the trip take?"
              value={motorbikeDuration}
              onChange={(value) => { setCustomValue("motorbike.duration", value); }}
              minHours={0.5}
              maxHours={8}
              rightCorner={<ModeSwitcher />}
            />
          ) : (<><DistanceLocationInput
            label="From"
            value={motorbikeFrom}
            placeholder={"Departure"}
            setCalculationError={setDistanceError}
            onChange={(value, country) => { setCustomValue("motorbike.origin", value); setCustomValue("motorbike.country", country) }}
          />
          <DistanceLocationInput
            label="To"
            value={motorbikeTo}
            placeholder={"Destination"}
            setCalculationError={setDistanceError}
            onChange={(value) => setCustomValue("motorbike.destination", value)}
          /></>)}
          ComponentUnder={<ModeSwitcher/>}
          useTravelTime={useTravelTime}
          distance={'motorbike.distance'}
        >
          <div className={styles.fieldRow}>
            <Select
              onChange={(value) => setCustomValue("motorbike.motorbikeType", value)}
              id="motorbike.motorbikeType"
              label="Motorbike type"
              optionsList={motorbikeTypes}
              register={register}
              selectedItem={motorbikeType}
            />
            <Select
              onChange={(value) => setCustomValue("motorbike.vehicleType", value)}
              id="motorbike.vehicleType"
              label="Vehicle type"
              optionsList={motorbikeVehicleTypes}
              register={register}
              selectedItem={motorbikeVehicleType}
            />
          </div>
        </TabContent>
      )}

      {display2Row[activeTab] && (
        <AppButton
          isLoading={isLoading}
          type='submit'
          style={{ width: '100%', marginTop: '1rem' }}
          disabled={calculatorMode === CALCULATOR_MODE.multicity && !isFlightsValid}
        >
          Calculate
        </AppButton>
      )}
      </div>
    </AppFormProvider>
  );
};

export default Tabs;
